import { UIEvent, useCallback } from 'react';

import * as ScrollAreaPrimitives from '@radix-ui/react-scroll-area';
import throttle from 'lodash/throttle';
import { twMerge } from 'tailwind-merge';

interface ScrollAreaProps extends ScrollAreaPrimitives.ScrollAreaViewportProps {
  readonly onEndReached?: () => void;
  readonly onEndReachedThreshold?: number;
}

function ScrollArea({ children, onScroll, onEndReached, onEndReachedThreshold = 0.5, className, ...props }: ScrollAreaProps) {
  const onEndReachedThrottled = onEndReached
    ? throttle(onEndReached, 500, {
        leading: true,
        trailing: false,
      })
    : undefined;

  const onViewportScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const top = (e.target as HTMLDivElement).scrollTop;
      const viewportHeight = (e.target as HTMLDivElement).clientHeight;
      const threshold = onEndReachedThreshold * viewportHeight;

      const height = (e.target as HTMLDivElement).scrollHeight - viewportHeight;

      if (top >= height - threshold) {
        onEndReachedThrottled?.();
      }

      onScroll?.(e);
    },
    [onEndReachedThrottled, onEndReachedThreshold, onScroll],
  );

  return (
    <ScrollAreaPrimitives.Root onScroll={onViewportScroll} className={twMerge('overflow-auto', className)}>
      <ScrollAreaPrimitives.Viewport {...props} className="h-full w-full">
        {children}
      </ScrollAreaPrimitives.Viewport>
      <ScrollAreaPrimitives.Scrollbar
        className="flex touch-none select-none bg-neutral-100 p-0.5 transition-colors hover:bg-neutral-200 radix-orientation-horizontal:h-3 radix-orientation-horizontal:flex-col radix-orientation-vertical:w-3"
        orientation="vertical"
      >
        <ScrollAreaPrimitives.Thumb className="relative flex-1 rounded-xl bg-neutral-600" />
      </ScrollAreaPrimitives.Scrollbar>
      <ScrollAreaPrimitives.Scrollbar
        className="flex touch-none select-none bg-neutral-100 p-0.5 transition-colors hover:bg-neutral-200 radix-orientation-horizontal:h-3 radix-orientation-horizontal:flex-col radix-orientation-vertical:w-3"
        orientation="horizontal"
      >
        <ScrollAreaPrimitives.Thumb className="relative flex-1 rounded-lg bg-neutral-500" />
      </ScrollAreaPrimitives.Scrollbar>
      <ScrollAreaPrimitives.Corner className="bg-neutral-300" />
    </ScrollAreaPrimitives.Root>
  );
}

export { ScrollArea };
